/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  Box,
  Checkbox,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  equipmentTypes,
  intermodalEquipmentTypes,
  loadModesTypes,
} from '@common/commonLists';
import { reeferEquipmentTypes } from '@common/order/constants';
import { SelectInput } from '@components/Select/SelectInput';
import {
  orderStore,
  setLoadSureStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { Show } from 'solid-js';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { FeatureFlags } from '@constants/featureFlags';

import { isLoadSureVendorAttached, loadId } from './LoadSureHelpers';
import { ReeferSettings } from './ReeferSettings';

type PropsT = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

export const LoadDetails = (props: PropsT) => {
  return (
    <Box class="min-w-0 pr-1">
      <Box mb={1} textAlign="left">
        <Typography
          variant="body1"
          class="text-base font-normal text-[#123B50] tracking-[0.00938em] text-armstrong-primary"
        >
          Load Details
        </Typography>
      </Box>
      <Stack direction="column" gap={2} textAlign="left">
        <Box>
          <SelectInput
            label="Mode"
            menuItems={loadModesTypes.map((mode) => {
              return {
                label: mode,
                value: mode,
              };
            })}
            onChange={(value: string) => {
              updateLoadPropertyAtIndex({
                // @ts-expect-error This is the same error in the old code.
                mode: value,
                cargoValueIsHigh: undefined,
                freightValue: undefined,
                teamRequired: value === 'LTL' ? false : props.load.teamRequired,
                secondDriver:
                  value === 'LTL' ? undefined : props.load.secondDriver,
                secondDriverPhoneNumber:
                  value === 'LTL'
                    ? undefined
                    : props.load.secondDriverPhoneNumber,
              });
            }}
            value={props.load.mode ?? ''}
            disabled={orderStore.isReadOnly}
            dataTestId="load-mode-select"
          />
        </Box>
        <Box textAlign="left">
          <SelectInput
            label="Equipment"
            menuItems={
              props.load.mode === 'Intermodal'
                ? intermodalEquipmentTypes.map((equipment) => {
                    return {
                      label: equipment,
                      value: equipment,
                    };
                  })
                : equipmentTypes
            }
            onChange={(value: string) => {
              updateLoadPropertyAtIndex({
                equipment: value,
              });
              if (isLoadSureVendorAttached()) {
                setLoadSureStore('isDirty', (prev) => [...prev, loadId()]);
              }
            }}
            value={props.load.equipment ?? ''}
            disabled={orderStore.isReadOnly}
            dataTestId="load-equipment-select"
          />
        </Box>
        <Show
          when={
            !!props.load.equipment &&
            reeferEquipmentTypes.includes(props.load.equipment)
          }
        >
          <Box>
            <ReeferSettings load={props.load} tabIndex={props.tabIndex} />
          </Box>
        </Show>
        <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
          <TextField
            label="Product"
            value={props.load.product ?? ''}
            onChange={(val: string) => {
              updateLoadPropertyAtIndex({
                product: val,
              });
            }}
            // size="sm"
            class="w-full"
            maxLength={30}
            disabled={orderStore.isReadOnly}
            dataTestId={'product-textInput'}
          />
        </Box>
        <Show
          when={
            !props.load.equipment ||
            !reeferEquipmentTypes.includes(props.load.equipment)
          }
        >
          <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
            <TextField
              label="Temperature Instructions"
              value={props.load.tempInstructions ?? ''}
              onChange={(val: string) =>
                updateLoadPropertyAtIndex({
                  tempInstructions: val,
                })
              }
              // size="sm"
              class="h-full w-full"
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError[
                  `loads[${props.tabIndex}].tempInstructions`
                ]
              }
              disabled={orderStore.isReadOnly}
              dataTestId={'temperature-instructions-textInput'}
            />
          </Box>
        </Show>
        <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
          <TextField
            label="Tracking Number"
            value={props.load.trackingNumber ?? ''}
            onChange={(val: string) =>
              updateLoadPropertyAtIndex({
                trackingNumber: val,
              })
            }
            maxLength={60}
            // size="sm"
            class="w-full"
            disabled={orderStore.isReadOnly}
            dataTestId={'tracking-number-textInput'}
          />
        </Box>
        <Stack direction="column" gap={2}>
          {props.load.mode !== 'Intermodal' ? (
            <>
              <Show when={props.load.mode !== 'LTL'}>
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                  label="Team"
                  checked={props.load.teamRequired}
                  onChange={(checked) => {
                    updateLoadPropertyAtIndex({
                      teamRequired: checked,
                      secondDriver: checked
                        ? props.load.secondDriver
                        : undefined,
                      secondDriverPhoneNumber: checked
                        ? props.load.secondDriverPhoneNumber
                        : undefined,
                    });
                  }}
                  disabled={orderStore.isReadOnly}
                  dataTestId={'team-checkboxInput'}
                />
              </Show>
              <Checkbox
                size="xs"
                color="info"
                class="ml-[-9px]"
                label="Over Dimensional"
                checked={props.load.od}
                onChange={(checked) => {
                  updateLoadPropertyAtIndex({
                    od: checked,
                  });
                }}
                disabled={orderStore.isReadOnly}
                dataTestId={'over-dimensional-checkboxInput'}
              />

              <Checkbox
                size="xs"
                color="info"
                class="ml-[-9px]"
                label="Tarps Required"
                checked={props.load.tarpRequired}
                onChange={(checked) => {
                  updateLoadPropertyAtIndex({
                    tarpRequired: checked,
                  });
                }}
                disabled={orderStore.isReadOnly}
                dataTestId={'tarps-required-checkboxInput'}
              />
            </>
          ) : null}

          <Checkbox
            size="xs"
            color="info"
            class="ml-[-9px]"
            label="Hazmat"
            checked={props.load.hazmatRequired}
            onChange={(checked) => {
              updateLoadPropertyAtIndex({
                hazmatRequired: checked,
              });
            }}
            disabled={orderStore.isReadOnly}
            dataTestId={'hazmat-checkboxInput'}
          />
          <Checkbox
            size="xs"
            color="info"
            class="ml-[-9px]"
            label="Ramps Required"
            checked={props.load.ramps}
            onChange={(checked) => {
              updateLoadPropertyAtIndex({
                ramps: checked,
              });
            }}
            disabled={orderStore.isReadOnly}
            dataTestId={'ramps-required-checkboxInput'}
          />
          <Show
            when={isFeatureFlagEnabled(
              FeatureFlags.feature_show_drop_trailer_accessorial,
            )()}
          >
            <Tooltip
              title={
                props.load.dropTrailer
                  ? 'Deselect Drop Trailer on all stops to remove.'
                  : 'Select Drop Trailer on any stop to apply.'
              }
              placement="bottom-start"
              class="z-10"
            >
              <Checkbox
                size="xs"
                color="info"
                class="ml-[-9px]"
                label="Drop Trailer"
                checked={props.load.dropTrailer}
                disabled={true}
                dataTestId={'drop-load-checkbox'}
              />
            </Tooltip>
          </Show>
        </Stack>
        <Box>
          <Typography
            variant="body2"
            sxProps={{ fontSize: '12px', fontWeight: '600' }}
            class="text-base font-bold text-armstrong-primary mb-[5px]"
          >
            AI POS NOTES
          </Typography>
          <Box
            style={{
              'max-height': '300px',
              'overflow-y': 'auto',
            }}
          >
            <ul>
              {props.load.truckBids?.map((bid) => {
                return bid.noteKeyValues.map((note) => {
                  return (
                    <li
                      style={{
                        'list-style': 'initial',
                        'margin-left': '20px',
                      }}
                    >
                      {note.key}: {note.value}
                    </li>
                  );
                });
              })}
            </ul>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
